import { IDynamicPartData } from '../entities';
import { ERoleHatItemId } from '../enums';
import { MatchTypeKey } from '../game-settings';

export interface IMapConfigEntry {
  id: string;
  type: MatchTypeKey;
  name: string;
  assetName: string;
  teamCount: number;
  teamSize: number;
  debugOnly?: boolean; // if false, wont show outside of debugmode
  unlockedByMapId?: string;
  lobbyConfig?: {
    title: string;
    subtitle: string;
    dataKey: string;
    unlockTooltip?: string;
  };
}

export interface ILane {
  id: string;
  team: number;
  points: { x: number; y: number }[];
}

/**
 * Map entity interface with loose string typing for id.
 * For type-safe entity IDs, use IMapEntity from @nova/configs instead.
 */
export interface IMapEntityLoose {
  id: string;
  x: number;
  y: number;
  tutorialTag?: string;
  team: number;
}

/**
 * Map entity bot interface with loose string typing for id.
 * For type-safe entity IDs, use IMapEntityBot from @nova/configs instead.
 */
export interface IMapEntityBotLoose extends IMapEntityLoose {
  parts?: Record<string, IDynamicPartData>;
  roleSkill: ERoleHatItemId;
}

export interface ISpawnSetLoose {
  team: number;
  laneId: string;
  entities: IMapEntityLoose[];
}

export interface ISpawnConfigLoose {
  spawnInterval: number;
  spawnSets: ISpawnSetLoose[];
}

export interface IRoomLoose {
  initEntities: IMapEntityLoose[];
  initBots?: IMapEntityBotLoose[];
}

export type RoomSetLoose = { [roomIndex: string]: IRoomLoose | undefined };

export interface IMapConfigEntryOrLoose extends IMapConfigEntry {
  rooms: RoomSetLoose;
}

export function isMapConfigEntryOrLoose(mapConfig: IMapConfigEntry): mapConfig is IMapConfigEntryOrLoose {
  return mapConfig.type === 'trenchRun'
    || 'rooms' in mapConfig;
}

export interface IMapConfigEntryDomination extends IMapConfigEntry {
  humanOpponents?: boolean;
  afkTimer?: number;
  gamePlayConfig: {
    [key: string]: number;
  };
  uiConfigs: IUIConfigDomination;
}

export function isMapConfigEntryDomination(mapConfig: IMapConfigEntry): mapConfig is IMapConfigEntryDomination {
  return mapConfig.type === 'captureMode'
    || 'humanOpponents' in mapConfig
    || 'afkTimer' in mapConfig
    || 'gamePlayConfig' in mapConfig
    || 'uiConfigs' in mapConfig;
}

export interface IMiniMapInfo {
  textureName: string;
  bgFrame: string;
  gameMapDimensions: {
    width: number;
    height: number;
  };
  entityZIndex: Record<string, number>;
}

export interface IUIConfigDomination {
  countDownTimes: number; // Countdown X seconds
  matchStartUiDelay: number; // X seconds delay before showing the match start UI sequence
  matchStartSoonUiDuration: number; // X seconds delay before starting countdown
  countdownInterval: number; // X second interval for countdown
  matchBeginDisplayDuration: number; // X seconds to display Match Begins!
  playerDisconnectDisplayDuration: number; // Display player disconnect message for X seconds

  miniMap: IMiniMapInfo;
}
